import SignatureCanvas from "react-signature-canvas";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  HStack,
  Divider,
  useToast,
  Skeleton,
} from "@chakra-ui/react";
import { supabase } from "index";
import {
  LoanSignatureCreateViewModel,
  LoanSignatureUpdateViewModel,
} from "@common/loans/signatures/loan-signature.model";
import { LoanSignature } from "@common/loans/signatures/loan-signature.model";
import { useContext, useEffect, useRef, useState } from "react";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useIpAddress } from "hooks/useIpAddress";
import { api } from "utils/api";
import { useCustomFormats } from "hooks/useCustomFormat";
import { LoanContext } from "providers/loan";

interface SignatureModalProps {
  type: "borrower" | "co-borrower";
  onClose: () => void;
  signature?: LoanSignature;
  onSignatureChange: (signature: LoanSignature) => void;
  signatureDataUrl?: string;
}

export function SignatureModal({
  type,
  onClose,
  signature,
  onSignatureChange,
  signatureDataUrl,
}: SignatureModalProps) {
  const { user } = useTenant();
  const { loan } = useContext(LoanContext);
  const { formatDateTime } = useCustomFormats();
  const [isSaving, setIsSaving] = useState(false);
  const signatureCanvas = useRef<SignatureCanvas>(null);
  const { data: ipAddress, isLoading: isIpAddressLoading } = useIpAddress();
  const toast = useToast();

  useEffect(() => {
    if (signatureDataUrl) {
      requestAnimationFrame(() => {
        signatureCanvas.current?.fromDataURL(signatureDataUrl);
      });
    }
  }, [signatureDataUrl]);

  const clearSignature = () => {
    signatureCanvas?.current?.clear();
  };

  const uploadSignature = async (
    lenderSigCanvas: React.RefObject<SignatureCanvas>
  ): Promise<{ filePath: string }> => {
    if (!lenderSigCanvas.current) {
      throw new Error("Lender signature canvas is not available");
    }

    if (lenderSigCanvas.current.isEmpty()) {
      throw new Error("Lender signature canvas is empty");
    }

    const dataURL = lenderSigCanvas.current.toDataURL("image/png");
    const blob = await fetch(dataURL).then(res => res.blob());
    const file = new File([blob], "signature.png", {
      type: "image/png",
    });

    const fileId = crypto.randomUUID();
    const filePath = `${loan!.id}/${fileId}.png`;

    const { error } = await supabase.storage.from("signatures").upload(filePath, file, {
      contentType: "image/png",
      upsert: true,
    });

    if (error) {
      throw error;
    }

    return { filePath };
  };

  const updateSignature = async (
    filePath: string,
    type: "borrower" | "co-borrower"
  ): Promise<LoanSignature> => {
    const signatureUpdate: LoanSignatureUpdateViewModel = {
      signature_url: filePath,
      user_ip_address: ipAddress!,
      type: type,
    };

    await api.put<LoanSignature>(`loans/${loan!.id}/signatures/${signature!.id}`, signatureUpdate);

    return {
      ...signature!,
      ...signatureUpdate,
    };
  };

  const addNewSignature = async (
    type: "borrower" | "co-borrower",
    filePath: string
  ): Promise<LoanSignature> => {
    const signatureCreate: LoanSignatureCreateViewModel = {
      loan: loan!.id,
      signature_url: filePath,
      user_ip_address: ipAddress!,
      type: type,
    };

    const signature = await api.post<LoanSignature>(
      `loans/${loan!.id}/signatures`,
      signatureCreate
    );

    return signature.data;
  };

  const saveSignature = async (type: "borrower" | "co-borrower"): Promise<LoanSignature> => {
    const { filePath } = await uploadSignature(signatureCanvas);

    if (signature) {
      return updateSignature(filePath, type);
    } else {
      return addNewSignature(type, filePath);
    }
  };

  const handleSaveSignature = async () => {
    if (signatureCanvas.current?.isEmpty() ?? true) {
      toast({
        title: "Error saving signature",
        description: "Please sign before saving",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);

    try {
      const signatureInfo = await saveSignature(type);
      onSignatureChange(signatureInfo);

      const title = `${type.charAt(0).toUpperCase() + type.slice(1)} Signature Saved`;

      toast({
        title: title,
        description: `Your ${type} signature has been successfully saved.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      toast({
        title: "Error creating signature",
        description: "There was a problem creating your signature data. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getSignatureInformationStack = (label: string, children: React.ReactNode | string) => {
    return (
      <HStack>
        <Text fontWeight="medium">{label}:</Text>
        {typeof children === "string" ? <Text>{children}</Text> : children}
      </HStack>
    );
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textTransform="capitalize">{type} Signature</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={2} color="gray.600">
            Please sign in the box below:
          </Text>
          <Box
            border="1px solid"
            borderColor={"gray.200"}
            borderRadius="md"
            width="100%"
            height="300px"
            position="relative"
            overflow="hidden"
          >
            <SignatureCanvas
              ref={signatureCanvas}
              penColor="black"
              minWidth={1}
              maxWidth={2.5}
              velocityFilterWeight={0.7}
              canvasProps={{
                width: 500,
                height: 300,
                className: "signature-canvas",
                style: {
                  width: "100%",
                  height: "100%",
                  cursor: "crosshair",
                },
              }}
              backgroundColor="rgba(255, 255, 255, 0)"
            />
          </Box>
          <Divider my={4} />
          <Box fontSize="sm">
            <Text fontWeight="bold" mb={2}>
              Signature Information:
            </Text>
            {getSignatureInformationStack("Name", user.first_name + " " + user.last_name)}
            {getSignatureInformationStack("Email", user.email)}
            {getSignatureInformationStack(
              "IP Address",
              isIpAddressLoading ? <Skeleton w="100px" h="20px" /> : <Text>{ipAddress}</Text>
            )}
            {signature && (
              <>{getSignatureInformationStack("Date/Time", formatDateTime(signature.created_at))}</>
            )}
            <Text mt={2} color="gray.500" fontSize="xs">
              This information will be stored with your signature
            </Text>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={clearSignature} isDisabled={isSaving}>
            Clear
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSaveSignature}
            isDisabled={isSaving}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
